import { getFromStorage } from 'reacticoon/storage'
import { getMuiOverrides } from './muiOverrides'

const type = getFromStorage('MUI::THEME::TYPE') || 'dark'

const palette = {
  type,
  primary: { main: '#3B82F6', light: '#60A5FA', dark: '#2563EB' },
  secondary: { main: '#10B981', light: '#34D399', dark: '#059669' },
  background: type === 'dark' 
    ? { default: '#0F172A', paper: '#1E293B' }
    : { default: '#F8FAFC', paper: '#FFFFFF' },
  text: type === 'dark'
    ? { primary: '#F1F5F9', secondary: '#94A3B8' }
    : { primary: '#1E293B', secondary: '#64748B' },
  error: { main: '#EF4444', light: '#F87171' },
  warning: { main: '#F59E0B', light: '#FBBF24' },
  success: { main: '#10B981', light: '#34D399' },
  info: { main: '#3B82F6', light: '#60A5FA' },
  divider: type === 'dark' ? 'rgba(148, 163, 184, 0.12)' : 'rgba(30, 41, 59, 0.12)',
}

const BREAKPOINT_KEYS = ['xs', 'sm', 'md', 'lg', 'xl']
const BREAKPOINT_VALUES = {
  xs: 0,      // Mobile portrait
  sm: 600,    // Mobile landscape / Tablet portrait
  md: 960,    // Tablet landscape / Desktop
  lg: 1280,   // Desktop
  xl: 1920,   // Large desktop
}
// Material-UI décale ses bornes hautes de 0.05px pour éviter qu'un `up` et un `down`
// adjacents ne se déclenchent tous les deux sur la même largeur.
const BREAKPOINT_STEP = 0.05

//
// ⚠️ `down(key)` signifie « JUSQU'EN DESSOUS DU PALIER SUIVANT », pas « en dessous de ce
// palier ». C'est la sémantique de Material-UI, et tout le code du site est écrit pour elle.
//
// L'implémentation précédente renvoyait `max-width: values[key] - 0.05`, soit un cran trop
// bas. Conséquences mesurées :
//   - `down('sm')`  donnait 599.95px au lieu de 959.95px → la plage 600–960px n'était
//     JAMAIS stylée, alors que 107 blocs de styles mobiles la visent. C'est aussi pourquoi
//     `useIsMobile()` restait faux sur tablette et petite fenêtre, laissant la barre
//     latérale déployée à 280px (Page/index.js bascule pourtant bien en Drawer temporaire).
//   - `down('xs')`  donnait `max-width: -0.05px`, qui ne correspond à AUCUN écran : tout
//     bloc écrit sous ce palier était du code mort silencieux.
//
// `between` et `only` étaient purement absents : le premier appel aurait planté.
//
const upMediaQuery = (key) => `@media (min-width:${BREAKPOINT_VALUES[key]}px)`

const downMediaQuery = (key) => {
  const nextIndex = BREAKPOINT_KEYS.indexOf(key) + 1
  const nextValue = BREAKPOINT_VALUES[BREAKPOINT_KEYS[nextIndex]]
  // Dernier palier : plus rien au-dessus, la règle vaut pour toutes les largeurs.
  if (nextIndex === BREAKPOINT_KEYS.length) {
    return upMediaQuery('xs')
  }
  return `@media (max-width:${nextValue - BREAKPOINT_STEP}px)`
}

const betweenMediaQuery = (start, end) => {
  const endIndex = BREAKPOINT_KEYS.indexOf(end) + 1
  if (endIndex === BREAKPOINT_KEYS.length) {
    return upMediaQuery(start)
  }
  return (
    `@media (min-width:${BREAKPOINT_VALUES[start]}px) and ` +
    `(max-width:${BREAKPOINT_VALUES[BREAKPOINT_KEYS[endIndex]] - BREAKPOINT_STEP}px)`
  )
}

const theme = {
  type,
  isDark: type === 'dark',
  palette,
  breakpoints: {
    keys: BREAKPOINT_KEYS,
    values: BREAKPOINT_VALUES,
    up: upMediaQuery,
    down: downMediaQuery,
    between: betweenMediaQuery,
    only: (key) =>
      BREAKPOINT_KEYS.indexOf(key) === BREAKPOINT_KEYS.length - 1
        ? upMediaQuery(key)
        : betweenMediaQuery(key, key),
  },
  spacing: (...args) => {
    if (args.length === 0) return 8
    if (args.length === 1) return args[0] * 8
    // Pour plusieurs arguments, retourner une string avec px
    return args.map(a => `${a * 8}px`).join(' ')
  },
  color: { 
    middlegrey: '#64748B',
    lightgrey: '#E2E8F0',
    red400: '#F87171',
    green400: '#34D399',
    purple300: '#C084FC',
  },
  status: {
    error: '#EF4444',
    warning: '#F59E0B',
    inactive: '#64748B',
    valid: '#10B981',
    info: '#3B82F6',
  },
  app: {
    background: { dark: type === 'dark' ? '#0F172A' : '#F8FAFC' },
    header: { height: '64px' },
    sidebar: { width: 280, color: type === 'dark' ? '#1E293B' : '#FFFFFF' },
    appBar: { height: '64px' },
    ticket: { sidebarWidth: 280 },
  },
  shape: { borderRadius: 12 },
  shadows: type === 'dark' ? [
    'none',
    '0 1px 3px 0 rgba(0, 0, 0, 0.4)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.4)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.4)',
  ] : [
    'none',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  ],
  style: {
    flexCenter: { display: 'flex', alignItems: 'center' },
    focusPrimary: {
      '&:focus': {
        backgroundColor: palette.primary.main,
        color: 'white',
        outline: '2px solid rgba(59, 130, 246, 0.5)',
        outlineOffset: '2px',
      },
    },
    chip: {
      borderRadius: '8px',
      borderColor: palette.primary.main,
      borderWidth: '1px',
      borderStyle: 'solid',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      backdropFilter: 'blur(8px)',
      cursor: 'pointer',
      height: 24,
      lineHeight: '24px',
      marginLeft: 5,
      padding: '0 12px',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        transform: 'translateY(-1px)',
      },
    },
    scrollbar: {
      '&::-webkit-scrollbar-corner': { background: 'rgba(0,0,0,0)' },
      '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      '&::-webkit-scrollbar': { width: '8px', backgroundColor: 'transparent' },
      '&::-webkit-scrollbar-thumb': { 
        borderRadius: '8px', 
        backgroundColor: 'rgba(148, 163, 184, 0.3)', 
        transition: 'background-color 0.2s ease', 
        '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.5)' } 
      },
    },
    glassMorphism: type === 'dark' ? {
      background: 'rgba(30, 41, 59, 0.8)',
      backdropFilter: 'blur(12px) saturate(180%)',
      border: '1px solid rgba(148, 163, 184, 0.12)',
      borderRadius: '12px',
    } : {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(12px) saturate(180%)',
      border: '1px solid rgba(30, 41, 59, 0.12)',
      borderRadius: '12px',
    },
  },
  gta: {
    colors: {
      green: '#47761E',
      yellow: '#FED985',
      salmon: '#F09E71',
      purple: '#933CE5',
      blue: '#61B5CB',
      lightGreen: '#93B592',
      rose: '#D5A0C4'
    }
  },
  typography: {
    fonts: {
      104: { style: { fontSize: 10, fontWeight: 400, margin: 0 } },
      124: { style: { fontSize: 12, fontWeight: 400, margin: 0 } },
      134: { style: { fontSize: 13, fontWeight: 400, margin: 0 } },
      137: { style: { fontSize: 13, fontWeight: 700, margin: 0 } },
      144: { style: { fontSize: 14, fontWeight: 400, margin: 0 } },
      147: { style: { fontSize: 14, fontWeight: 700, margin: 0 } },
      164: { style: { fontSize: 16, fontWeight: 400, margin: 0 } },
      167: { style: { fontSize: 16, fontWeight: 700, margin: 0 } },
      184: { style: { fontSize: 18, fontWeight: 400 } },
      187: { style: { fontSize: 18, fontWeight: 700, margin: 0 } },
      204: { style: { fontSize: 20, fontWeight: 400, margin: 0 } },
      207: { style: { fontSize: 20, fontWeight: 700, margin: 0 } },
      224: { style: { fontSize: 22, fontWeight: 400, margin: 0 } },
      240: { style: { fontSize: 24, fontWeight: 400, margin: 0 } },
      247: { style: { fontSize: 24, fontWeight: 700, margin: 0 } },
    },
  },
  overrides: getMuiOverrides({ palette }),
}

// Set the data-theme attribute on the document root for CSS variables
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', type)
}

export default theme
